import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { setAuth } from '../lib/auth';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Auth = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ 
    name: '', 
    email: '', 
    password: '',
    acceptPrivacy: false,
    acceptTerms: false,
    emailUpdates: false
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, loginForm);
      console.log('Login response:', response.data);
      
      const authStored = setAuth(response.data.token, response.data.user);
      if (!authStored) {
        toast.error('Failed to store authentication. Please try again.');
        setLoading(false);
        return;
      }
      
      toast.success('Welcome back!');
      
      // Small delay to ensure localStorage is written
      setTimeout(() => {
        navigate('/');
        window.location.reload(); // Force reload to update auth state
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!registerForm.acceptPrivacy) {
      toast.error('Please accept the Privacy Policy');
      return;
    }
    
    if (!registerForm.acceptTerms) {
      toast.error('Please accept the Terms & Conditions');
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/register`, registerForm);
      console.log('Register response:', response.data);
      
      const authStored = setAuth(response.data.token, response.data.user);
      if (!authStored) {
        toast.error('Failed to store authentication. Please try again.');
        setLoading(false);
        return;
      }
      
      toast.success('Account created successfully!');
      
      // Small delay to ensure localStorage is written
      setTimeout(() => {
        navigate('/profile/edit');
        window.location.reload(); // Force reload to update auth state
      }, 100);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 flex items-center justify-center py-8 px-4">
      <Card className="w-full max-w-md p-8 bg-white shadow-lg">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-slate-900 mb-2" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            DigiDreamSolutions
          </h1>
          <p className="text-slate-600">Your Complete Digital Business Ecosystem</p>
        </div>

        <Tabs defaultValue="login" className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="login">Login</TabsTrigger>
            <TabsTrigger value="register">Register</TabsTrigger>
          </TabsList>

          <TabsContent value="login">
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={loginForm.email}
                  onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-center">
                  <Label htmlFor="login-password">Password</Label>
                  <a 
                    href="/forgot-password" 
                    className="text-sm text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Forgot Password?
                  </a>
                </div>
                <Input
                  id="login-password"
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="login-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>
          </TabsContent>

          <TabsContent value="register">
            <form onSubmit={handleRegister} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="register-name">Name</Label>
                <Input
                  id="register-name"
                  value={registerForm.name}
                  onChange={(e) => setRegisterForm({ ...registerForm, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  data-testid="register-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  type="email"
                  value={registerForm.email}
                  onChange={(e) => setRegisterForm({ ...registerForm, email: e.target.value })}
                  placeholder="your@email.com"
                  required
                  data-testid="register-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="register-password">Password</Label>
                <Input
                  id="register-password"
                  type="password"
                  value={registerForm.password}
                  onChange={(e) => setRegisterForm({ ...registerForm, password: e.target.value })}
                  placeholder="••••••••"
                  minLength={6}
                  required
                  data-testid="register-password"
                />
              </div>
              
              {/* Checkboxes */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="acceptPrivacy"
                    checked={registerForm.acceptPrivacy}
                    onChange={(e) => setRegisterForm({ ...registerForm, acceptPrivacy: e.target.checked })}
                    className="mt-1 w-4 h-4 text-blue-600 rounded"
                    required
                  />
                  <label htmlFor="acceptPrivacy" className="text-sm text-slate-700">
                    I accept the{' '}
                    <a href="/privacy-policy" target="_blank" className="text-blue-600 hover:underline">
                      Privacy Policy
                    </a>
                    <span className="text-red-500">*</span>
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={registerForm.acceptTerms}
                    onChange={(e) => setRegisterForm({ ...registerForm, acceptTerms: e.target.checked })}
                    className="mt-1 w-4 h-4 text-blue-600 rounded"
                    required
                  />
                  <label htmlFor="acceptTerms" className="text-sm text-slate-700">
                    I agree to the{' '}
                    <a href="/terms-conditions" target="_blank" className="text-blue-600 hover:underline">
                      Terms & Conditions
                    </a>
                    <span className="text-red-500">*</span>
                  </label>
                </div>

                <div className="flex items-start gap-2">
                  <input
                    type="checkbox"
                    id="emailUpdates"
                    checked={registerForm.emailUpdates}
                    onChange={(e) => setRegisterForm({ ...registerForm, emailUpdates: e.target.checked })}
                    className="mt-1 w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="emailUpdates" className="text-sm text-slate-700">
                    By checking this box, you will receive our updates and promotional emails
                  </label>
                </div>
              </div>
              
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full"
                data-testid="register-submit"
              >
                {loading ? 'Creating account...' : 'Create Account'}
              </Button>
            </form>
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
};

export default Auth;
